import React from 'react';
import {
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SocialPlatform } from '../types';
import { getPlatform, getPlatformName } from '../config/platforms';
import { TimingAdvice } from './TimingAdvice';
import { SameDayWarning } from './SameDayWarning';
import { bestTimesFor, formatTime, WEEKDAYS, weekdayOf } from '../config/postingSchedule';

export type ScheduleVariants = Record<string, string>;

interface Props {
  /** Platforms the post targets. A platform removed here loses its time with it. */
  platforms: string[];
  /** The time platforms without their own entry publish at. */
  base: Date | null;
  /** Per-platform overrides, ISO strings keyed by platform. */
  variants: ScheduleVariants;
  onBaseChange: (value: Date | null) => void;
  onVariantsChange: (value: ScheduleVariants) => void;
  /** Hide the base picker where the caller already renders one. */
  hideBase?: boolean;
  /** The post being rescheduled, so the same-day warning does not report it against itself. */
  postId?: string;
  disabled?: boolean;
}

/** A time that has already passed cannot be scheduled — the server refuses it. */
const isPast = (value: Date | null) => value !== null && value.getTime() <= Date.now();

/**
 * Choose when a post goes out, either once for everything or per network.
 *
 * The per-network half exists because the best moment to post is not the same on every
 * platform — the posting schedule says so per network, and until now a scheduled post had a
 * single time that forced LinkedIn, Facebook and X into the same slot. Guided Post worked
 * around it by creating a separate post per network, which meant editing the wording three
 * times.
 *
 * `base` is the time any platform without its own entry uses, so a post with the switch off
 * behaves exactly as it always did; the server applies the same rule. Turning the switch on
 * seeds every platform from the base rather than leaving blanks, so the first thing shown is
 * the current schedule rather than an empty form.
 *
 * Shared by the reschedule dialog and the edit dialog so the two cannot drift — a time set in
 * one and invisible in the other is the sort of thing that gets found in production.
 */
export const PlatformSchedule: React.FC<Props> = ({
  platforms,
  base,
  variants,
  onBaseChange,
  onVariantsChange,
  hideBase = false,
  disabled = false,
  postId,
}) => {
  const staggered = Object.keys(variants).length > 0;

  /**
   * "Use each network's best time": the day is chosen once and every network's time comes
   * from the posting schedule for that weekday (Ian, 2026-10-19). Turning it off keeps the
   * times it set, as ordinary per-network times to adjust.
   */
  const [auto, setAuto] = React.useState(false);
  const best = React.useMemo(
    () => (auto && base ? bestTimesFor(platforms as SocialPlatform[], base) : []),
    [auto, base, platforms]
  );

  // Only worth offering when there is more than one network to separate
  const canStagger = platforms.length > 1;

  /**
   * Keep the overrides in step with the selected platforms.
   *
   * Platforms can be added and removed while this is open (the edit dialog offers
   * checkboxes), and an override for a platform the post no longer targets would be invisible
   * here and then silently reappear if that platform were selected again.
   */
  React.useEffect(() => {
    if (!staggered || auto) return;
    const next: ScheduleVariants = {};
    let changed = false;
    platforms.forEach(p => {
      if (variants[p]) next[p] = variants[p];
      else if (base) {
        next[p] = base.toISOString();
        changed = true;
      }
    });
    if (changed || Object.keys(next).length !== Object.keys(variants).length) {
      onVariantsChange(next);
    }
  }, [platforms, staggered, auto, base, variants, onVariantsChange]);

  // In auto mode the times follow the day and the networks: the earliest becomes the base,
  // and with more than one network each gets its own entry. Only written when something
  // differs, so this settles after one pass.
  React.useEffect(() => {
    if (!auto || !base || !best.length) return;
    const earliest = best.reduce((a, b) => (b.when < a.when ? b : a)).when;
    if (earliest.getTime() !== base.getTime()) onBaseChange(earliest);
    const next: ScheduleVariants = {};
    if (best.length > 1) best.forEach(b => { next[b.platform] = b.when.toISOString(); });
    const same = Object.keys(next).length === Object.keys(variants).length
      && Object.entries(next).every(([k, v]) => variants[k] && new Date(variants[k]).getTime() === new Date(v).getTime());
    if (!same) onVariantsChange(next);
  }, [auto, base, best, variants, onBaseChange, onVariantsChange]);

  const toggleStagger = (on: boolean) => {
    if (!on) {
      onVariantsChange({});
      return;
    }
    // Seed from the time already chosen, so turning this on changes nothing until a
    // network's time is actually moved.
    const seed: ScheduleVariants = {};
    const from = base || new Date(Date.now() + 60 * 60 * 1000);
    platforms.forEach(p => {
      seed[p] = from.toISOString();
    });
    if (!base) onBaseChange(from);
    onVariantsChange(seed);
  };

  const setPlatformTime = (platform: string, value: Date | null) => {
    if (!value) return;
    onVariantsChange({ ...variants, [platform]: value.toISOString() });
  };

  const toggleAuto = (on: boolean) => {
    setAuto(on);
    // Starting from nothing, offer tomorrow rather than an empty picker
    if (on && !base) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      onBaseChange(tomorrow);
    }
  };

  const dayName = base ? WEEKDAYS.find(d => d.key === weekdayOf(base))?.label : '';

  const pastPlatforms = platforms.filter(p => variants[p] && isPast(new Date(variants[p])));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {platforms.length > 0 && (
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={auto}
              disabled={disabled}
              onChange={e => toggleAuto(e.target.checked)}
            />
          }
          label="Use each network's best time"
        />
      )}

      {auto && (
        <DatePicker
          label="Publish on"
          value={base}
          onChange={d => d && onBaseChange(d)}
          disablePast
          disabled={disabled}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: 'Each network goes out at its time from the Posting Schedule for that day.',
            },
          }}
        />
      )}

      {auto && best.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {[...best].sort((a, b) => a.when.getTime() - b.when.getTime()).map(b => {
            const config = getPlatform(b.platform);
            return (
              <Box key={b.platform} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={getPlatformName(b.platform)}
                  sx={{ bgcolor: config?.color, color: '#fff', fontWeight: 600, minWidth: 84 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatTime(b.time)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {b.theme
                    ? `${dayName}'s ${b.theme} slot`
                    : `no ${dayName} slot, so its usual time`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {!hideBase && !auto && (
        <DateTimePicker
          label={staggered ? 'Default time' : 'Publish at'}
          value={base}
          onChange={onBaseChange}
          disablePast
          disabled={disabled}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: staggered
                ? 'Used by any network without its own time below.'
                : undefined,
            },
          }}
        />
      )}

      {canStagger && !auto && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              checked={staggered}
              disabled={disabled || !base}
              onChange={e => toggleStagger(e.target.checked)}
            />
          }
          label="Use a different time for each network"
        />
      )}

      {staggered && !auto && (
        <Box sx={{ mt: 1 }}>
          {platforms.map(platform => {
            const config = getPlatform(platform as SocialPlatform);
            const value = variants[platform] ? new Date(variants[platform]) : base;
            return (
              <Box key={platform} sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    size="small"
                    label={getPlatformName(platform as SocialPlatform)}
                    sx={{
                      bgcolor: config?.color,
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <DateTimePicker
                  label={`${getPlatformName(platform as SocialPlatform)} publishes at`}
                  value={value}
                  onChange={v => setPlatformTime(platform, v)}
                  disablePast
                  disabled={disabled}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
                {/* Advice for this network alone: a time that suits Facebook but not X is
                    exactly the trade-off per-network times exist to resolve. */}
                <TimingAdvice
                  platforms={[platform as SocialPlatform]}
                  when={value}
                  onPick={d => setPlatformTime(platform, d)}
                />
              </Box>
            );
          })}
        </Box>
      )}

      {!staggered && !auto && base && (
        <TimingAdvice
          platforms={platforms as SocialPlatform[]}
          when={base}
          onPick={onBaseChange}
        />
      )}

      {/* Rescheduling onto an occupied day deserves the same note as composing onto one. */}
      {base && (
        <SameDayWarning
          platforms={platforms as SocialPlatform[]}
          when={base}
          excludePostId={postId}
        />
      )}

      {pastPlatforms.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {pastPlatforms.map(p => getPlatformName(p as SocialPlatform)).join(', ')}
          {pastPlatforms.length === 1 ? ' has' : ' have'} a time that has already passed.
          Choose a future time, or use Publish now.
        </Alert>
      )}

      {(staggered || (auto && best.length > 1)) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Each network publishes at its own time. The post stays in the queue until the last
          one has gone out, and Post History shows what has published so far.
        </Typography>
      )}
    </LocalizationProvider>
  );
};

/** Times in the past, which the server refuses. Exported so dialogs can block Save. */
export const pastScheduleEntries = (
  platforms: string[],
  variants: ScheduleVariants
): string[] => platforms.filter(p => variants[p] && isPast(new Date(variants[p])));
